import {
    AlertToastType,
    BadgeColor,
    BadgeShape,
    BadgeSize,
    ExBadge,
    ExCheckbox,
    ExIconButton,
    ExPill,
    IconButtonFlavor,
    IconButtonSize,
    IconButtonType,
    PillColor,
    ToastController,
} from '@boomi/exosphere';
import { useEffect, useState } from 'react';
import { type Connector, getConnector } from '../connectors/connectors';
import { GENERIC_ERROR_MESSAGE } from '../shared/apiUtils';
import ErrorAlertBanner from '../shared/ErrorAlertBanner';
import styles from '../shared/Form.module.css';
import Table, { type TableColumn } from '../shared/Table';
import toolStyles from './ConnectorTools.module.css';
import { type Connection, updateConnection } from './connections';
import { getTools, type Tool } from './tools';

interface ConnectorToolsProps {
    mode?: 'create' | 'edit';
    connectorId: string;
    disabledTools: string[];
    onDisabledToolsChange: (disabledTools: string[]) => void;
    onToolCountChange: (count: number) => void;
    connection?: Connection;
}

const ConnectorTools = ({
    mode = 'create',
    connectorId,
    disabledTools,
    onDisabledToolsChange,
    onToolCountChange,
    connection,
}: ConnectorToolsProps) => {
    const [connector, setConnector] = useState<Connector | null>(null);
    const [tools, setTools] = useState<Tool[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setLoading(true);
        Promise.all([getConnector(connectorId), getTools(connectorId)])
            .then(([connectorData, toolsData]) => {
                const sorted = [...toolsData].sort((a, b) =>
                    a.annotations.title.localeCompare(b.annotations.title),
                );
                setConnector(connectorData);
                setTools(sorted);
                onToolCountChange(sorted.length);
            })
            .catch(() => setError(GENERIC_ERROR_MESSAGE))
            .finally(() => setLoading(false));
    }, [connectorId, onToolCountChange]);

    const areAllSelected = tools.length > 0 && tools.every((t) => selected.has(t.name));
    const areSomeSelected = tools.some((t) => selected.has(t.name));

    const toggleSelectAll = () => {
        if (areAllSelected) {
            setSelected(new Set());
        } else {
            setSelected(new Set(tools.map((t) => t.name)));
        }
    };

    const toggleRow = (name: string) =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(name)) {
                next.delete(name);
            } else {
                next.add(name);
            }
            return next;
        });

    const applyDisabledTools = async (newDisabledTools: string[]) => {
        if (mode === 'create') {
            onDisabledToolsChange(newDisabledTools);
            setSelected(new Set());
            return;
        }
        if (!connection) return;
        if (saving) return;
        setSaving(true);
        try {
            await updateConnection(connection.appId, {
                ...connection,
                disabledTools: newDisabledTools,
            });
            onDisabledToolsChange(newDisabledTools);
            setSelected(new Set());
            ToastController.show({
                description: 'Tools updated successfully.',
                type: AlertToastType.SUCCESS,
            });
        } catch (error) {
            console.error('Failed to update tools:', error);
            ToastController.show({
                description: 'Failed to update tools. Please try again.',
                type: AlertToastType.ERROR,
            });
        } finally {
            setSaving(false);
        }
    };

    const handleEnable = () => {
        void applyDisabledTools(disabledTools.filter((name) => !selected.has(name)));
    };

    const handleDisable = () => {
        const newlyDisabled = Array.from(selected).filter((name) => !disabledTools.includes(name));
        void applyDisabledTools([...disabledTools, ...newlyDisabled]);
    };

    if (error) {
        return <ErrorAlertBanner onClose={() => setError(null)}>{error}</ErrorAlertBanner>;
    }

    const selectedCount = selected.size;

    const columns: TableColumn<Tool>[] = [
        {
            header: (
                <ExCheckbox
                    checked={areAllSelected}
                    indeterminate={areSomeSelected && !areAllSelected}
                    onChange={toggleSelectAll}
                    aria-label="Select all tools"
                />
            ),
            render: (tool: Tool) => (
                <ExCheckbox
                    checked={selected.has(tool.name)}
                    onChange={() => toggleRow(tool.name)}
                    aria-label={`Select ${tool.annotations.title}`}
                />
            ),
            width: '40px',
        },
        {
            header: 'Tool Name',
            searchValue: (tool) => `${tool.annotations.title} ${tool.name}`,
            render: (tool) => (
                <div className={toolStyles.toolNameCell}>
                    {connector?.icon && (
                        <img
                            src={connector.icon}
                            alt={connector.name}
                            className={toolStyles.connectorIcon}
                        />
                    )}
                    <div className={toolStyles.toolNameInfo}>
                        <span className={toolStyles.toolTitle}>{tool.annotations.title}</span>
                        <span className={toolStyles.toolId}>{tool.name}</span>
                    </div>
                </div>
            ),
        },
        {
            header: 'Description',
            searchValue: (tool) => tool.description,
            render: (tool) => tool.description,
            cellProps: (tool) => ({
                className: toolStyles.toolDescription,
                title: tool.description,
            }),
        },
        {
            header: 'Status',
            render: (tool) => (
                <ExBadge
                    color={!disabledTools.includes(tool.name) ? BadgeColor.GREEN : BadgeColor.RED}
                    size={BadgeSize.SMALL}
                    shape={BadgeShape.SQUARED}
                >
                    {!disabledTools.includes(tool.name) ? 'Active' : 'Inactive'}
                </ExBadge>
            ),
        },
    ];

    const toolbar =
        selectedCount > 0 ? (
            <div className={toolStyles.toolbar}>
                <ExPill
                    color={PillColor.BLUE}
                    selected
                    interactive
                    removable
                    onRemove={() => setSelected(new Set())}
                >
                    {selectedCount} Tool{selectedCount !== 1 ? 's' : ''} selected
                </ExPill>
                <div className={toolStyles.toolbarActions}>
                    <ExIconButton
                        data-pendo-button="config-toolslist-enable"
                        icon="Success"
                        label="Enable selected tools"
                        tooltipText="Enable selected tools"
                        type={IconButtonType.TERTIARY}
                        flavor={IconButtonFlavor.BASE}
                        size={IconButtonSize.DEFAULT}
                        circular
                        disabled={saving}
                        onClick={handleEnable}
                    />
                    <ExIconButton
                        data-pendo-button="config-toolslist-disable"
                        icon="Disabled"
                        label="Disable selected tools"
                        tooltipText="Disable selected tools"
                        type={IconButtonType.TERTIARY}
                        flavor={IconButtonFlavor.RISKY}
                        size={IconButtonSize.DEFAULT}
                        circular
                        disabled={saving}
                        onClick={handleDisable}
                    />
                </div>
            </div>
        ) : undefined;

    return (
        <>
            {mode === 'create' && (
                <h3 className={styles.sectionHeading}>Manage Tools ({tools.length})</h3>
            )}
            <Table
                columns={columns}
                items={tools}
                rowKey={(tool) => tool.name}
                rowClassName={(tool) =>
                    selected.has(tool.name) ? toolStyles.selectedRow : undefined
                }
                itemLabel="tools"
                searchPendoTag="config-toolslist-search"
                loading={loading}
                toolbar={toolbar}
            />
        </>
    );
};

export default ConnectorTools;
